import { MutableRefObject, createContext, useRef } from "react";

import type { APIResponseEvent } from "../api/apiContext.ts";
import { REQUEST_STATUS } from "../api/requestStatus.ts";
import { useResponseSubscriber } from "../api/useResponseSubscriber.ts";
import { GenericDocumentHeader } from "../types/documentHeader.ts";

type DocumentHeaderRefContext = {
  [key: string]: GenericDocumentHeader;
};

export const DocumentHeaderRefContext =
  createContext<MutableRefObject<DocumentHeaderRefContext> | null>(null);

type Props = {
  children: React.ReactNode;
};

export function DocumentHeaderRefProvider({ children }: Props) {
  const headerRef = useRef<DocumentHeaderRefContext>({});

  useResponseSubscriber((action) =>
    saveHeaders(headerRef.current, action as FIXME),
  );

  return (
    <DocumentHeaderRefContext.Provider value={headerRef}>
      {children}
    </DocumentHeaderRefContext.Provider>
  );
}

type PotentialDocumentResponse = {
  documentHeader?: GenericDocumentHeader;
  documentList?: {
    documentHeader: GenericDocumentHeader;
  }[];
};

function saveHeaders(
  ref: DocumentHeaderRefContext,
  action: APIResponseEvent<unknown, PotentialDocumentResponse>,
) {
  if (action.type !== REQUEST_STATUS.FINISHED) {
    return;
  }

  if (action.data?.documentHeader !== undefined) {
    const key = action.data.documentHeader.id;

    ref[key] = getDocumentHeader(ref[key], action.data.documentHeader);
    return;
  }

  if (Array.isArray(action.data?.documentList)) {
    action.data.documentList.forEach((document) => {
      const key = document.documentHeader?.id;
      if (!key) return;

      ref[key] = getDocumentHeader(ref[key], document.documentHeader);
    });

    return;
  }
}

function getDocumentHeader(
  oldDocumentHeader: GenericDocumentHeader,
  documentHeader: GenericDocumentHeader,
) {
  if (oldDocumentHeader === undefined) {
    return documentHeader;
  }

  if (
    documentHeader._etag !== oldDocumentHeader._etag &&
    documentHeader._ts > oldDocumentHeader._ts
  ) {
    return documentHeader;
  }

  return oldDocumentHeader;
}
